import type {
  CreditOverviewUiState,
  CreditSubscriptionStatus,
  CreditTimelineStep,
} from './credit-overview.model';

const STATUS_RANK: Record<CreditSubscriptionStatus, number> = {
  NOT_STARTED: 0,
  TERMS_ACCEPTED: 1,
  VERIFY_DIT: 2,
  VERIFY_DIT_FAILED: 2,
  VERIFY_SMFA: 3,
  VERIFY_SMFA_FAILED: 3,
  VERIFY_SMFA_TIMEDOUT: 3,
  ENROLLED: 4,
  FULFILLED: 5,
};

const FAILED_STATUSES = new Set<CreditSubscriptionStatus>([
  'VERIFY_DIT_FAILED',
  'VERIFY_SMFA_FAILED',
  'VERIFY_SMFA_TIMEDOUT',
]);

export function isInProgressStatus(status: CreditSubscriptionStatus): boolean {
  return (
    status === 'TERMS_ACCEPTED' ||
    status === 'VERIFY_DIT' ||
    status === 'VERIFY_SMFA' ||
    status === 'ENROLLED'
  );
}

export function toCreditOverviewUiState(
  status: CreditSubscriptionStatus,
): CreditOverviewUiState {
  if (status === 'NOT_STARTED') return 'not_linked';
  if (status === 'FULFILLED') return 'linked';
  if (FAILED_STATUSES.has(status)) return 'failed';
  return 'in_progress';
}

export function buildCreditTimeline(
  status: CreditSubscriptionStatus,
): CreditTimelineStep[] {
  const currentRank = STATUS_RANK[status];
  const failed = FAILED_STATUSES.has(status);
  const afterFailureSubtext = failed ? 'Not reached' : undefined;

  const stateAt = (rank: number): CreditTimelineStep['state'] => {
    if (rank < currentRank) return 'complete';
    if (rank > currentRank) return 'pending';
    return failed ? 'failed' : 'current';
  };

  return [
    { id: 'not_started', title: 'Not started', state: stateAt(0) },
    { id: 'terms_agreed', title: 'Terms agreed', state: stateAt(1) },
    {
      id: 'verify_dit',
      title: 'Identity verified · DIT',
      state: stateAt(2),
      subtext:
        currentRank > 2 ? 'Personal details confirmed with Equifax' : undefined,
    },
    {
      id: 'verify_smfa',
      title:
        failed && currentRank === 3
          ? 'Identity check · SMFA'
          : 'Phone & identity check · SMFA',
      state: stateAt(3),
      subtext:
        status === 'VERIFY_SMFA'
          ? 'Knowledge-based questions in progress. SMS link expires in 4 min.'
          : status === 'VERIFY_SMFA_FAILED' || status === 'VERIFY_SMFA_TIMEDOUT'
            ? 'Knowledge-based questions not passed; SMS link expired before completion.'
            : failed && currentRank < 3
              ? afterFailureSubtext
              : undefined,
    },
    {
      id: 'enrolled',
      title: 'Enrolled with Equifax',
      state: stateAt(4),
      subtext:
        status === 'ENROLLED'
          ? 'Awaiting credit data fulfillment'
          : afterFailureSubtext,
    },
    {
      id: 'report_ready',
      title: 'Report ready',
      state: stateAt(5),
      subtext: failed
        ? 'Not reached'
        : 'Score & report populate in-app and here',
    },
  ];
}
